import React, {useCallback, useMemo} from 'react';
import {Dialog, DialogProps, IconButton, Slide} from '@mui/material';
import styled from 'styled-components';
import {Close} from '@mui/icons-material';
import {COLORS, FONTS, SIZES, Typography} from '@fupa/fupa-uikit';
import {styled as muiStyled} from '@mui/material/styles';
import {SoftTickerData, SoftTickerState} from 'app/reducers/softTickerReducer';
import UpdateResultSuccess from 'app/components/match/liveResult/UpdateSuccess';
import {closeSoftTicker, loadSoftTickerData, openAddScorer} from 'app/actions/softTickerActions';
import {useSoftTickerContext} from 'app/hooks/useSoftTickerContext';
import UpdateResult from 'app/components/match/liveResult/UpdateResult';
import UpdateScorer from 'app/components/match/liveResult/updateScorer/UpdateScorer';
import {MatchEventGoalResource} from 'app/components/match/liveResult/updateScorer/sharedTypes';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';
import {TransitionProps} from '@mui/material/transitions';
import useAllowSoftTicker, {useAllowSoftTickerEvents} from 'app/hooks/useAllowSoftTicker';
import {useStoreSofttickerDataInSessionStorage} from 'app/hooks/useStoreSofttickerDataInSessionStorage';
import useNativeReloadSoftticker from 'app/hooks/useNativeReloadSoftticker';

const Header = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-content: center;
`;

const Body = styled.div`
  padding: ${SIZES['32']} ${SIZES['16']} ${SIZES['16']};
  display: flex;
  flex-direction: column;
  gap: ${SIZES['16']};

  @media only screen and (max-width: 340px) {
    padding: ${SIZES['24']} 0 ${SIZES['8']};
  }
`;

const Title = styled(Typography)`
  &&& {
    font-size: ${FONTS['18']};
    align-self: center;
  }
`;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} children={props.children} />;
});

interface CustomDialogProps extends DialogProps {
  height?: number;
}

const CustomDialog = muiStyled(Dialog, {
  shouldForwardProp: prop => prop !== 'height',
})<CustomDialogProps>(({height, theme}) => ({
  '& .MuiDialog-container': {
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-end',
    },
  },
  '& .MuiPaper-root': {
    margin: 0,
    padding: SIZES['8'],
    width: '100%',
    height: height ? `${height}px` : 'auto',
    transition: 'height 0.1s',
    maxHeight: '100%',
  },
}));

const dialogTitleByState = {
  [SoftTickerState.Result]: 'Live-Spielstand aktualisieren',
  [SoftTickerState.ResultSuccess]: 'Live-Spielstand aktualisieren',
  [SoftTickerState.ScorerAdd]: 'Torschütze hinzufügen',
  [SoftTickerState.ScorerUpdate]: 'Torschütze bearbeiten',
};

type CloseEventByState = {
  [key: string]: (view: string | null) => {event: string; view?: string};
};

const closeEventByState: CloseEventByState = {
  [SoftTickerState.Result]: () => ({event: 'live_result'}),
  [SoftTickerState.ResultSuccess]: () => ({event: 'live_result', view: 'layer_report_result_success'}),
  [SoftTickerState.ScorerAdd]: view => ({event: 'scorer', view: view ?? undefined}),
};

interface SoftTickerDialogProps {
  match: any;
  tickerEvents: any[];
  onMatchChange: (updateFunc: () => void) => void;
  onScorerChange: (matchEvent: MatchEventGoalResource, isUpdate: boolean) => void;
  onScorerDelete: (matchEventId: number) => void;
}
const SoftTickerDialog = ({
  match,
  tickerEvents,
  onMatchChange,
  onScorerChange,
  onScorerDelete,
}: SoftTickerDialogProps) => {
  const {softTickerData, dispatch} = useSoftTickerContext();

  const initializeSoftTickerData = useCallback(
    (data: SoftTickerData) => dispatch(loadSoftTickerData(data)),
    [dispatch, loadSoftTickerData]
  );

  useStoreSofttickerDataInSessionStorage(softTickerData, initializeSoftTickerData);
  useNativeReloadSoftticker(softTickerData.open);

  const closeDialog = () => {
    dispatch(closeSoftTicker());
  };

  const handleUpdateScorer = (matchEvent: MatchEventGoalResource, isUpdate: boolean) => {
    closeDialog();
    onScorerChange(matchEvent, isUpdate);
  };

  const handleDeleteScorer = (matchEventId: number) => {
    closeDialog();
    onScorerDelete(matchEventId);
  };

  const handleResultResumeToScorer = () => {
    sendEventToGA4('soft_ticker_report_scorer', {
      soft_ticker_action: 'open',
      ui_view: 'layer_report_result_success',
      match_slug: match.slug,
    });
    dispatch(openAddScorer('layer_report_scorer'));
  };

  const handleClose = (event?: string, view?: string) => {
    if (event) {
      const eventData = {
        live_result: {
          name: 'report_live_result',
          ui_view: 'layer_report_live_result',
        },
        scorer: {
          name: 'report_scorer',
          ui_view: 'layer_report_scorer',
        },
      };

      sendEventToGA4(`soft_ticker_${eventData[event].name}`, {
        soft_ticker_action: 'cancel',
        ui_view: view ?? eventData[event].ui_view,
        match_slug: match.slug,
      });
    }
    closeDialog();
  };

  const handleScorerClose = () => {
    const view = softTickerData.initiatorView ?? undefined;
    handleClose('scorer', view);
  };

  const renderChild = {
    [SoftTickerState.Result]: (
      <UpdateResult match={match} onClose={() => handleClose('live_result')} onResultChange={onMatchChange} />
    ),
    [SoftTickerState.ResultSuccess]: (
      <UpdateResultSuccess
        match={match}
        tickerEvents={tickerEvents}
        onClose={() => handleClose('live_result', 'layer_report_result_success')}
        onResume={handleResultResumeToScorer}
      />
    ),
    [SoftTickerState.ScorerAdd]: (
      <UpdateScorer match={match} onClose={handleScorerClose} onUpdateScorer={handleUpdateScorer} />
    ),
    [SoftTickerState.ScorerUpdate]: (
      <UpdateScorer
        match={match}
        onClose={() => handleClose()}
        onUpdateScorer={handleUpdateScorer}
        onDelete={handleDeleteScorer}
      />
    ),
  };

  const handleDialogClose = () => {
    const eventData = closeEventByState[softTickerData.state]?.(softTickerData.initiatorView) ?? {};
    handleClose(eventData.event, eventData.view);
  };

  const height = useMemo(() => {
    switch (softTickerData.state) {
      case SoftTickerState.Result:
      case SoftTickerState.ResultSuccess:
        return 421;
      case SoftTickerState.ScorerAdd:
      case SoftTickerState.ScorerUpdate:
        return 580;
      default:
        return 0;
    }
  }, [softTickerData.state]);
  const allowSoftTicker = useAllowSoftTicker(match);
  const allowSoftTickerEvents = useAllowSoftTickerEvents(match);

  return allowSoftTicker || allowSoftTickerEvents ? (
    <CustomDialog
      TransitionComponent={Transition}
      onClose={handleDialogClose}
      open={softTickerData.open}
      height={height}>
      <Header>
        <IconButton color='inherit' onClick={handleDialogClose} aria-label='close'>
          <Close sx={{color: COLORS.darkGrey, fontSize: 20}} />
        </IconButton>
        <Title variant='title2'>{dialogTitleByState[softTickerData.state]}</Title>
      </Header>
      <Body>{renderChild[softTickerData.state]}</Body>
    </CustomDialog>
  ) : null;
};

export default SoftTickerDialog;
